import React, { useState, useEffect } from 'react';
import { Modal, Form, Button, ButtonGroup } from 'react-bootstrap';
import axios from 'axios';
import { useTranslation } from 'react-i18next';

import { useResto } from '../contexts/RestoContext';
import config from '../config/config';
import { useAuth } from '../contexts/AuthContext';
import { setFlashMessage, useWebsite } from '../contexts/WebsiteContext';

const RestoParameters = () => {
    const { t } = useTranslation();
    const { restoData, setRestoData } = useResto();
    const [editedResto, setEditedResto] = useState({ ...restoData });
    const [showModal, setShowModal] = useState(false);
    const { setIsLoading } = useWebsite();
    const { token } = useAuth();

    useEffect(() => {
        setEditedResto({ ...restoData });
    }, [restoData]);

    const handleEditInputChange = (e) => {
        const { name, value } = e.target;
        setEditedResto(prevState => ({
            ...prevState,
            [name]: value,
        }));
    };

    const languageLabels = {
        'fr': 'Français',
        'en': 'Anglais',
        'es': 'Espagnol',
        'de': 'Allemand'
    };

    const handleEditSubmit = async () => {
        setIsLoading(true);
        try {
            const response = await axios.put(
                `${config.apiUrl}resto/${restoData.website}`,
                editedResto,
                {
                    headers: {
                        Authorization: `Bearer ${token}`
                    }
                }
            );

            setRestoData(response.data);
            handleCloseEditModal();
            setFlashMessage(t('SUCCESS_UPDATED_MESSAGE'), 'success');
        } catch (error) {
            setFlashMessage(t('ERROR_MESSAGE'), 'error');
        }
        setIsLoading(false);
    };

    const handleShowModal = () => {
        setShowModal(true);
    };

    const handleCloseEditModal = () => {
        setShowModal(false);
    };

    const mainColorStyle = {
        margin: '5px',
        border: 'solid 1px black', 
        width: '50px',
        height: '50px',
        backgroundColor: `rgb(${editedResto.mainColor})`,
    };

    const color2Style = {
        margin: '5px',
        border: 'solid 1px black', 
        width: '50px',
        height: '50px',
        backgroundColor: `rgb(${editedResto.color2})`,
    };

    const color3Style = {
        margin: '5px',
        border: 'solid 1px black', 
        width: '50px',
        height: '50px',
        backgroundColor: `rgb(${editedResto.color3})`,
    };

    function componentToHex(c) {
        if (!c) return "80";
        const hex = c.toString(16);
        return hex.length === 1 ? "0" + hex : hex;
    }
    
    function rgbToHex(rgb) {
        const [r, g, b] = rgb.split(',').map(val => parseInt(val));
        return componentToHex(r) + componentToHex(g) + componentToHex(b);
    }
    
    function hexToRgb(hex) {
        if (!hex) return "128,128,128";

        const bigint = parseInt(hex, 16);
        const r = (bigint >> 16) & 255;
        const g = (bigint >> 8) & 255;
        const b = bigint & 255;
        return `${r},${g},${b}`;
    }

    const languageFlags = ['fr', 'en', 'es', 'de'];

    const handleLanguageToggle = (lang) => {
        if (lang === 'fr') {
            return;
        }
    
        const updatedLanguages = [...editedResto.availableLanguages];
    
        const langIndex = updatedLanguages.indexOf(lang);
    
        if (langIndex !== -1) {
            updatedLanguages.splice(langIndex, 1);
        } else {
            updatedLanguages.push(lang);
        }
    
        setEditedResto(prevState => ({
            ...prevState,
            availableLanguages: updatedLanguages,
        }));
    };

    return (
        <>
            <h2>Le site web</h2>

            <div className="kubuni-frame">
                <p><strong>Message d'alerte:</strong> {restoData.alert || <i>Inactif</i>}</p>
                <p><strong>Libellé du restaurant:</strong> {restoData.label}</p>
                <p><strong>Téléphone du restaurant:</strong> {restoData.phone || <i>Non renseigné</i>}</p>
                
                <p>
                    <strong style={{marginRight: '10px'}}>Langues activées:</strong>
                    {restoData.availableLanguages.map(lang => (
                        <img
                            key={lang}
                            src={`${config.kubuniImagesUrl}general-icons/flag_${lang}_icon_24.png`}
                            alt={lang}
                            style={{ marginRight: '5px' }}
                        />
                    ))}
                </p>    

                <p><strong>URL TripAdvisor:</strong> {restoData.socialMediaTripAdvisorUrl || <i>Inactif</i>}</p>
                <p><strong>URL Instagram:</strong> {restoData.socialMediaInstagramUrl || <i>Inactif</i>}</p>
                <p><strong>URL YouTube:</strong> {restoData.socialMediaYoutubeUrl || <i>Inactif</i>}</p>
                <p><strong>URL Facebook:</strong> {restoData.socialMediaFacebookUrl || <i>Inactif</i>}</p>
                
                <div style={{ display: 'flex', alignItems: 'center' }}>
                    <strong>Couleur principale du site:</strong>
                    <div style={{ marginLeft: '10px', ...mainColorStyle }}></div>
                </div>
                <div style={{ display: 'flex', alignItems: 'center' }}>
                    <strong>Couleur du fond des pages:</strong>
                    <div style={{ marginLeft: '10px', ...color2Style }}></div>
                </div>
                <div style={{ display: 'flex', alignItems: 'center' }}>
                    <strong>Couleur des bandes latérales:</strong>
                    <div style={{ marginLeft: '10px', ...color3Style }}></div>
                </div>
                <p><strong>Affichage de la (ou les) carte(s) sous la barre de supérieure:</strong> {restoData.hasHeaderMenu ? 'Oui' : 'Non'}</p>
                <p><strong>Affichage de la (ou les) carte(s) sous la barre de navigation:</strong> {restoData.hasNavigationMenu ? 'Oui' : 'Non'}</p>
                <p><strong>Affichage du logo dans le pied de page:</strong> {restoData.hasFooterLogo ? 'Oui' : 'Non'}</p>
                <p><strong>Forme des icônes "Réseaux sociaux":</strong> {restoData.hasSquareSocialMediaIcons ? 'Carrée' : 'Ronde'}</p>
                
                <Button variant="primary" onClick={handleShowModal} style={{margin:'20px'}}>
                    Mettre à jour
                </Button>
            </div>
            <div className="short-line"></div>

            <Modal show={showModal} onHide={handleCloseEditModal} size="lg">
                <Modal.Header closeButton>
                    <Modal.Title>Mettre à jour les paramètres du site web</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <Form>
                        
                    <Form.Group>
                            <Form.Label>Message d'alerte</Form.Label>
                            <Form.Control
                                type="text"
                                name="alert"
                                value={editedResto.alert}
                                onChange={handleEditInputChange}
                            />
                        </Form.Group>
                        
                        <Form.Group>
                            <Form.Label>Libellé du restaurant</Form.Label>
                            <Form.Control
                                type="text"
                                name="label"
                                value={editedResto.label}
                                onChange={handleEditInputChange}
                            />
                        </Form.Group>

                        <Form.Group>
                            <Form.Label>Téléphone du restaurant</Form.Label>
                            <Form.Control
                                type="text"
                                name="phone"
                                value={editedResto.phone}
                                onChange={handleEditInputChange}
                            />
                        </Form.Group>

                        <Form.Group>
                            <Form.Label>Langues activées</Form.Label>
                            <ButtonGroup style={{ marginLeft: '5px' }}>
                                {languageFlags.map(lang => (
                                    <Button
                                        key={lang}
                                        variant="primary"
                                        onClick={() => handleLanguageToggle(lang)}
                                        className={`${lang === "fr" ? "activated-noclic" : editedResto.availableLanguages.includes(lang) ? "activated" : ""}`}
                                    >
                                        {languageLabels[lang]}
                                    </Button>
                                ))}
                            </ButtonGroup>

                        </Form.Group>

                        <Form.Group>
                            <Form.Label>URL TripAdvisor</Form.Label>
                            <Form.Control
                                type="text"
                                name="socialMediaTripAdvisorUrl"
                                value={editedResto.socialMediaTripAdvisorUrl}
                                onChange={handleEditInputChange}
                            />
                        </Form.Group>

                        <Form.Group>
                            <Form.Label>URL Instagram</Form.Label>
                            <Form.Control
                                type="text"
                                name="socialMediaInstagramUrl"
                                value={editedResto.socialMediaInstagramUrl}
                                onChange={handleEditInputChange}
                            />
                        </Form.Group>
                        
                        <Form.Group>
                            <Form.Label>URL YouTube</Form.Label>
                            <Form.Control
                                type="text"
                                name="socialMediaYoutubeUrl"
                                value={editedResto.socialMediaYoutubeUrl}
                                onChange={handleEditInputChange}
                            />
                        </Form.Group>

                        <Form.Group>
                            <Form.Label>URL Facebook</Form.Label>
                            <Form.Control
                                type="text"
                                name="socialMediaFacebookUrl"
                                value={editedResto.socialMediaFacebookUrl}
                                onChange={handleEditInputChange}
                            />
                        </Form.Group>

                        <Form.Group>
                            <Form.Label>Couleur du site</Form.Label>
                            <div style={{ display: 'flex', alignItems: 'center' }}>
                                <input
                                    type="color"
                                    value={`#${rgbToHex(editedResto.mainColor)}`}
                                    onChange={(e) => {
                                        const rgbValue = hexToRgb(e.target.value.substr(1));
                                        handleEditInputChange({ target: { name: 'mainColor', value: rgbValue } });
                                    }}
                                    style={{ marginLeft: '10px', cursor:'pointer' }}
                                />
                                <input
                                    type="hidden"
                                    value={editedResto.mainColor}
                                    onChange={(e) => {
                                        const rgbValue = e.target.value;
                                        handleEditInputChange({ target: { name: 'mainColor', value: rgbValue } });
                                    }}
                                />
                            </div>
                        </Form.Group>

                        <Form.Group>
                            <Form.Label>Couleur du fond des pages</Form.Label>
                            <div style={{ display: 'flex', alignItems: 'center' }}>
                                <input
                                    type="color"
                                    value={`#${rgbToHex(editedResto.color2)}`}
                                    onChange={(e) => {
                                        const rgbValue = hexToRgb(e.target.value.substr(1));
                                        handleEditInputChange({ target: { name: 'color2', value: rgbValue } });
                                    }}
                                    style={{ marginLeft: '10px', cursor:'pointer' }}
                                />
                                <input
                                    type="hidden"
                                    value={editedResto.color2}
                                    onChange={(e) => {
                                        const rgbValue = e.target.value;
                                        handleEditInputChange({ target: { name: 'color2', value: rgbValue } });
                                    }}
                                />
                            </div>
                        </Form.Group>

                        <Form.Group>
                            <Form.Label>Couleur du fond des pages</Form.Label>
                            <div style={{ display: 'flex', alignItems: 'center' }}>
                                <input
                                    type="color"
                                    value={`#${rgbToHex(editedResto.color3)}`}
                                    onChange={(e) => {
                                        const rgbValue = hexToRgb(e.target.value.substr(1));
                                        handleEditInputChange({ target: { name: 'color3', value: rgbValue } });
                                    }}
                                    style={{ marginLeft: '10px', cursor:'pointer' }}
                                />
                                <input
                                    type="hidden"
                                    value={editedResto.color3}
                                    onChange={(e) => {
                                        const rgbValue = e.target.value;
                                        handleEditInputChange({ target: { name: 'color3', value: rgbValue } });
                                    }}
                                />
                            </div>
                        </Form.Group>

                        <Form.Group>
                            <Form.Label>Afficher la (ou les) carte(s) sous la barre supérieure</Form.Label>
                            <ButtonGroup style={{ marginLeft: '5px' }}>
                                <Button
                                    variant="primary"
                                    active={!!editedResto.hasHeaderMenu}
                                    onClick={() => handleEditInputChange({ target: { name: 'hasHeaderMenu', value: true } })}
                                >
                                    Oui
                                </Button>
                                <Button
                                    variant="primary"
                                    active={!editedResto.hasHeaderMenu}
                                    onClick={() => handleEditInputChange({ target: { name: 'hasHeaderMenu', value: false } })}
                                >
                                    Non
                                </Button>
                            </ButtonGroup>
                        </Form.Group>

                        <Form.Group>
                            <Form.Label>Afficher la (ou les) carte(s) sous la barre de navigation</Form.Label>
                            <ButtonGroup style={{ marginLeft: '5px' }}>
                                <Button
                                    variant="primary"
                                    active={!!editedResto.hasNavigationMenu}
                                    onClick={() => handleEditInputChange({ target: { name: 'hasNavigationMenu', value: true } })}
                                >
                                    Oui
                                </Button>
                                <Button
                                    variant="primary"
                                    active={!editedResto.hasNavigationMenu}
                                    onClick={() => handleEditInputChange({ target: { name: 'hasNavigationMenu', value: false } })}
                                >
                                    Non
                                </Button>
                            </ButtonGroup>
                        </Form.Group>

                        <Form.Group>
                            <Form.Label>Afficher le logo dans le pied de page</Form.Label>
                            <ButtonGroup style={{ marginLeft: '5px' }}>
                                <Button
                                    variant="primary"
                                    active={!!editedResto.hasFooterLogo}
                                    onClick={() => handleEditInputChange({ target: { name: 'hasFooterLogo', value: true } })}
                                >
                                    Oui
                                </Button>
                                <Button
                                    variant="primary"
                                    active={!editedResto.hasFooterLogo}
                                    onClick={() => handleEditInputChange({ target: { name: 'hasFooterLogo', value: false } })}
                                >
                                    Non
                                </Button>
                            </ButtonGroup>
                        </Form.Group>

                        <Form.Group>
                            <Form.Label>Forme des icones "Réseaux sociaux"</Form.Label>
                            <ButtonGroup style={{ marginLeft: '5px' }}>
                                <Button
                                    variant="primary"
                                    active={!!editedResto.hasSquareSocialMediaIcons}
                                    onClick={() => handleEditInputChange({ target: { name: 'hasSquareSocialMediaIcons', value: true } })}
                                >
                                    Carrée
                                </Button>
                                <Button
                                    variant="primary"
                                    active={!editedResto.hasSquareSocialMediaIcons}
                                    onClick={() => handleEditInputChange({ target: { name: 'hasSquareSocialMediaIcons', value: false } })}
                                >
                                    Ronde
                                </Button>
                            </ButtonGroup>
                        </Form.Group>

                    </Form>
                </Modal.Body>
                <Modal.Footer className="justify-content-center">
                    <Button variant="secondary" onClick={handleCloseEditModal}>
                        { t('CANCEL') }
                    </Button>
                    <Button variant="primary" onClick={handleEditSubmit}>
                        { t('CONFIRM') }
                    </Button>
                </Modal.Footer>
            </Modal>
        </>
    );
};

export default RestoParameters;
